import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTwitter } from '@soundxyz/font-awesome/free-brands-svg-icons/index.mjs';
import { NULL_ADDRESS } from '@soundxyz/utils/const';
import type { ReferralSource } from '@soundxyz/utils/constants/embeds';
import { Button } from '@components/main-components/button/Button';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import blurImage from '@components/main-components/image/blur-image';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { useModal } from '@context/modal';
import {
  type FragmentType,
  getFragment,
  SpotifyConnectionReleaseInfoFragmentDoc,
} from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { useUrlWithAuthReferral } from '@hooks/useUrlWithReferral';
import { trackClickEvent } from '@lib/analytics';
import { getUrlPathForTwitter } from '@utils/helpers';

const COVER_SIZE = 120;

const sourceLink: ReferralSource = {
  link: true,
};

export function SpotifyConnectionSuccessModal({
  releaseFrag,
}: {
  releaseFrag: FragmentType<SpotifyConnectionReleaseInfoFragmentDoc>;
}) {
  const { closeCancelModal } = useModal();

  const release = getFragment(SpotifyConnectionReleaseInfoFragmentDoc, releaseFrag);

  const coverImageCdnUrl = release.gaCoverImage?.url ?? release.coverImage.url;

  const { isShareReferrable } = useCheckReferrableRelease({
    release,
  });

  const { urlWithReferral, referralAddress } = useUrlWithAuthReferral({
    url: release.webappUri,
    source: sourceLink,
    isReferrable: isShareReferrable,
  });

  const artistHandle = !!release.artist.user.twitterHandle
    ? `@${release.artist.user.twitterHandle}`
    : release.artist.name;

  const shareMessage = `I just presaved "${release.title}" from ${artistHandle} on @soundxyz_ #SoundFreeClaim`;

  const onTwitterShareClick = () => {
    trackClickEvent({
      event: 'Share to Twitter Clicked',
      properties: {
        associationId: release.id,
        associationType: 'Release',
        referralAddress: (referralAddress || NULL_ADDRESS).toLowerCase(),
        sourceComponent: 'Spotify Pre-Save Success',
      },
    });

    const path = getUrlPathForTwitter({
      type: 'presave',
      url: urlWithReferral,
      message: encodeURIComponent(shareMessage),
    });
    window.open(path, '_newtab');
  };

  const body = (
    <div className="flex w-full flex-col items-center text-center text-base800">
      <NextFutureImage
        className="rounded-lg"
        alt="cover image"
        src={coverImageCdnUrl}
        placeholder="blur"
        blurDataURL={blurImage(COVER_SIZE, COVER_SIZE)}
        width={COVER_SIZE}
        height={COVER_SIZE}
      />
      <h2 className="mt-4 font-title text-title-l font-medium">A forever edition is on the way!</h2>
      <p className="mt-3 text-base-l md:px-4">It's free! We've covered the artist and gas fees.</p>
      <p className="mb-8 mt-4 text-base-l md:px-4">
        It make take some time; we'll notify you when it's in your wallet.
      </p>
      <Button
        variant="primary"
        label="Share"
        leadingIcon={<FontAwesomeIcon icon={faTwitter} />}
        onClick={onTwitterShareClick}
        fullWidth
      />
    </div>
  );

  return <StandardModal body={body} closeModal={closeCancelModal} />;
}
